import axios from "axios";
import { useEffect, useRef } from "react";
import { roundPrice } from "../../tracking/create-ecommerce-event";
import { VouchersDistributionsRepository } from "../api/vouchers-distribution.api";
import type { FinalEventClaim, FinalEventName, GetOrderDTO } from "../api/vouchers-distribution.types";
import type { Order, VouchersOrderStatus } from "../models/order";
import { eventMissingEvent, orderCancelledEvent, safeTrackEvent } from "../tracking/events";
import type { ConfigName } from "../types/config-name";
import { clearVdCheckoutAttribution } from "../utils/vd-checkout-attribution";

// REV-125: Thank You Page final event (purchase / order_cancelled) gated by a durable backend claim.
// Flow: claim -> dataLayer push -> confirm. sessionStorage only remembers an unconfirmed push so a
// reload re-sends confirm instead of pushing twice; the backend claim stays the source of truth.

const repository = new VouchersDistributionsRepository();

const FINAL_STATUSES: readonly VouchersOrderStatus[] = ["completed", "processed", "cancelled"];
const EXPECTED_REJECTION_STATUSES = [409, 422];
const CLAIM_NOT_FOUND_STATUS = 404;
// Shared by the claim and confirm requests: up to 3 attempts, waiting 1 s, then 2 s.
const REQUEST_ATTEMPTS = 3;
const REQUEST_RETRY_DELAY_MS = 1000;

type StoredFinalEvent = { claimToken: string; confirmed: boolean };

// The backend dedupes per order + event (an order can go cancelled -> completed, so
// order_cancelled and purchase are independent claims), so the storage key carries both.
const storageKey = (uuid: string, event: FinalEventName) => `vd_final_event:${uuid}:${event}`;

// completed/processed are both terminal "paid" outcomes and expect a `purchase` claim.
const expectedFinalEvent = (status: VouchersOrderStatus): FinalEventName =>
  status === "cancelled" ? "order_cancelled" : "purchase";

const readStored = (uuid: string, event: FinalEventName): StoredFinalEvent | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(uuid, event));
    return raw ? (JSON.parse(raw) as StoredFinalEvent) : null;
  } catch {
    return null;
  }
};

const writeStored = (uuid: string, event: FinalEventName, value: StoredFinalEvent) => {
  try {
    sessionStorage.setItem(storageKey(uuid, event), JSON.stringify(value));
  } catch {
    // sessionStorage unavailable (private mode): the backend lease still prevents duplicates.
  }
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isClaimNotFound = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === CLAIM_NOT_FOUND_STATUS;

// Resolves true once the claim is settled and later reloads may stop re-sending confirm.
const confirmWithRetry = async (dto: GetOrderDTO, claimToken: string): Promise<boolean> => {
  for (let attempt = 1; attempt <= REQUEST_ATTEMPTS; attempt += 1) {
    try {
      await repository.confirmFinalEvent({ ...dto, claimToken });
      return true;
    } catch (error) {
      // 404 claim_not_found: the token was taken over or is unknown, so retrying can never succeed.
      if (isClaimNotFound(error)) return true;
      if (attempt < REQUEST_ATTEMPTS) await wait(REQUEST_RETRY_DELAY_MS * attempt);
    }
  }
  return false;
};

// Network errors and 5xx only: any 4xx is the backend's final answer for this visit. Retrying is
// safe because the backend dedupes: if a lost response hid a successful claim, the retry gets
// claim_in_progress and pushes nothing.
const isRetryableClaimError = (error: unknown) =>
  axios.isAxiosError(error) && (!error.response || error.response.status >= 500);

const claimWithRetry = async (dto: GetOrderDTO): Promise<FinalEventClaim> => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await repository.claimFinalEvent(dto);
    } catch (error) {
      if (attempt >= REQUEST_ATTEMPTS || !isRetryableClaimError(error)) throw error;
      await wait(REQUEST_RETRY_DELAY_MS * attempt);
    }
  }
};

// `event` is the backend's decision for this claim (from the claim response, or — for an
// unconfirmed record found on reload — the event that record was stored under).
const confirmAndStore = async (dto: GetOrderDTO, event: FinalEventName, claimToken: string) => {
  writeStored(dto.uuid, event, { claimToken, confirmed: false });
  if (await confirmWithRetry(dto, claimToken)) writeStored(dto.uuid, event, { claimToken, confirmed: true });
};

const isExpectedRejection = (error: unknown) =>
  axios.isAxiosError(error) && EXPECTED_REJECTION_STATUSES.includes(error.response?.status ?? 0);

// Checkout attribution is consumed only by the purchase push, so it is dropped once the TYP settles
// without one. claim_in_progress / order_pending keep it: a purchase may still come in this tab.
const SETTLED_WITHOUT_PURCHASE_CODES = ["already_reported", "not_eligible", "no_final_event"];

const isSettledWithoutPurchase = (error: unknown) =>
  axios.isAxiosError<{ error?: string }>(error) &&
  isExpectedRejection(error) &&
  SETTLED_WITHOUT_PURCHASE_CODES.includes(error.response?.data?.error ?? "");

type ReportFinalEventOptions = {
  slug: string;
  configName: ConfigName;
  order: Order;
  onPurchase: (order: Order) => void;
  errorContext: string;
};

export const reportFinalEvent = async ({
  slug,
  configName,
  order,
  onPurchase,
  errorContext,
}: ReportFinalEventOptions) => {
  const dto: GetOrderDTO = { slug, configName, uuid: order.uuid };
  const expectedEvent = expectedFinalEvent(order.status);
  const stored = readStored(order.uuid, expectedEvent);

  if (stored?.confirmed) return;
  if (stored) {
    await confirmAndStore(dto, expectedEvent, stored.claimToken);
    return;
  }

  let claim: FinalEventClaim;
  try {
    claim = await claimWithRetry(dto);
  } catch (error) {
    if (isSettledWithoutPurchase(error)) clearVdCheckoutAttribution();
    if (!isExpectedRejection(error)) {
      eventMissingEvent({
        expectedEventName: expectedEvent,
        errorContext: `${errorContext}: ${String(error)}`,
      });
    }
    return;
  }

  // "pushed" means handed to pushGtmEvent: it may still queue the push until this page's page_view
  // (300 ms failsafe), so the actual dataLayer.push can land shortly after the confirm request.
  const pushed =
    claim.event === "purchase"
      ? safeTrackEvent(() => onPurchase(order), "purchase", errorContext)
      : safeTrackEvent(
          () =>
            orderCancelledEvent({
              transactionId: order.uuid,
              value: roundPrice(Number(order.totalGrossPrice)),
              currency: "PLN",
            }),
          "order_cancelled",
          errorContext,
        );

  // An unpushed claim stays unconfirmed: its lease expires and the next visit can take it over.
  if (!pushed) return;

  if (claim.event === "order_cancelled") clearVdCheckoutAttribution();
  await confirmAndStore(dto, claim.event, claim.claimToken);
};

type UseFinalOrderEventOptions = Omit<ReportFinalEventOptions, "order"> & { order: Order | undefined };

// `onPurchase` must be stable (module-level function), otherwise the effect re-runs on every render.
export const useFinalOrderEvent = ({
  slug,
  configName,
  order,
  onPurchase,
  errorContext,
}: UseFinalOrderEventOptions) => {
  // Keyed on uuid + expected event (not just uuid): a status flip within the same mount
  // (cancelled -> completed) must be treated as a new attempt, not a repeat of the old one.
  const reportedKeyRef = useRef<string>();

  useEffect(() => {
    if (!slug || !order || !FINAL_STATUSES.includes(order.status)) return;
    const key = `${order.uuid}:${expectedFinalEvent(order.status)}`;
    if (reportedKeyRef.current === key) return;

    reportedKeyRef.current = key;
    void reportFinalEvent({ slug, configName, order, onPurchase, errorContext });
  }, [slug, configName, order, onPurchase, errorContext]);
};
